// Translates an OS-delivered URL (Universal Link on iOS, App Link on Android,
// or the custom `app.forgeapps.main://` scheme) into an in-app router path.
//
// The native side only hands us URLs it has been configured to claim, so the
// four places below must stay in sync — a path claimed natively but missing
// here opens the app on whatever screen it was already showing:
//
//   - frontend/android/app/src/main/AndroidManifest.xml  (<data android:pathPrefix>)
//   - frontend/ios/App/App/App.entitlements              (associated domains)
//   - frontend/public/.well-known/apple-app-site-association
//   - frontend/public/.well-known/assetlinks.json        (host + signing certs)

/** Custom scheme registered by both native apps — matches the Capacitor appId. */
export const NATIVE_URL_SCHEME = "app.forgeapps.main";

/** Host the native apps claim https links for. */
export const DEEP_LINK_HOST = "app.gomash.ai";

/**
 * Path prefixes claimed as Universal Links / App Links. Deliberately a short
 * list of externally shareable routes rather than the whole site: anything not
 * listed keeps opening in the browser, which is what we want for marketing and
 * auth pages.
 *
 * `/reset-password` in particular must stay unclaimed — Supabase puts the
 * recovery tokens in the URL fragment, and the browser flow already handles it.
 */
export const DEEP_LINK_PATH_PREFIXES = [
  "/join/",
  "/invite/",
  "/app-invite/",
  "/app/",
];

/** Hosts whose https links we treat as our own. */
function knownHosts(): Set<string> {
  const hosts = new Set([DEEP_LINK_HOST]);

  const appUrl = import.meta.env.VITE_APP_URL;
  if (appUrl) {
    try {
      hosts.add(new URL(appUrl).hostname);
    } catch {
      // Malformed env value — fall back to the static host above.
    }
  }

  // Covers local dev (app.localhost) and bundled-mode webview origins.
  if (typeof window !== "undefined" && window.location?.hostname) {
    hosts.add(window.location.hostname);
  }

  return hosts;
}

/**
 * Convert an incoming URL into a router path (`/join/ABC123?x=1`), or `null`
 * when the URL is not ours to handle.
 *
 * Returns `null` for the OAuth callback (`app.forgeapps.main://oauth-callback`),
 * which the auth store consumes separately.
 */
export function parseDeepLink(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  let path: string;

  if (url.protocol === `${NATIVE_URL_SCHEME}:`) {
    // Custom scheme: `app.forgeapps.main://join/ABC123` parses with "join" as
    // the host, so the path has to be reassembled. Also the shape used by
    // `adb shell am start -d …` / `xcrun simctl openurl` when testing.
    if (url.hostname === "oauth-callback") return null;
    path = `/${url.hostname}${url.pathname}`;
  } else if (url.protocol === "http:" || url.protocol === "https:") {
    if (!knownHosts().has(url.hostname)) return null;
    path = url.pathname;
  } else {
    return null;
  }

  path = path.replace(/\/{2,}/g, "/");

  if (!DEEP_LINK_PATH_PREFIXES.some(prefix => path.startsWith(prefix))) {
    console.warn("[DeepLink] Unclaimed path, ignoring:", path);
    return null;
  }

  return `${path}${url.search}${url.hash}`;
}
